import { useCallback, useState } from 'react';
import { Box, List, Tab, Tabs, Typography } from '@mui/material';

import type { SummaryReport } from '../../../types.ts';
import SummaryReportsDialogRow from '../../SummaryReportsDialogRow/SummaryReportsDialogRow.tsx';
import { dialogListSx, dialogTabsRailSx, dialogTabsSx } from '../dialogStyles.ts';

interface ReportListProps {
    reports: SummaryReport[];
    userId: string;
    tab: number;
    onTabChange: (tab: number) => void;
    onOpen: (report: SummaryReport) => void;
    onShare: (report: SummaryReport) => void;
    /** Both resolve true once the write has landed, so the row closes only on success. */
    onCommitRename: (report: SummaryReport, name: string, description?: string) => Promise<boolean>;
    onConfirmDelete: (report: SummaryReport) => Promise<boolean>;
}

export default function ReportList({
    reports,
    userId,
    tab,
    onTabChange,
    onOpen,
    onShare,
    onCommitRename,
    onConfirmDelete,
}: ReportListProps) {
    const [editingReportId, setEditingReportId] = useState<string | null>(null);
    const [pendingDeleteReport, setPendingDeleteReport] = useState<SummaryReport | null>(null);

    const startRename = useCallback((report: SummaryReport) => {
        setPendingDeleteReport(null);
        setEditingReportId(report.id);
    }, []);

    const commitRename = useCallback(
        async (report: SummaryReport, name: string, description?: string) => {
            if (await onCommitRename(report, name, description)) setEditingReportId(null);
        },
        [onCommitRename]
    );

    const confirmDelete = useCallback(
        async (report: SummaryReport) => {
            if (await onConfirmDelete(report)) setPendingDeleteReport(null);
        },
        [onConfirmDelete]
    );

    const visibleReports = reports.filter((r) => {
        if (tab === 0) return r.createdBy === userId;
        return r.visibility === 'shared' && r.createdBy !== userId;
    });

    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={dialogTabsRailSx}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => onTabChange(v)}
                    variant='fullWidth'
                    sx={dialogTabsSx}
                >
                    <Tab label='My Reports' />
                    <Tab label='Shared With Me' />
                </Tabs>
            </Box>
            <List dense sx={dialogListSx}>
                {visibleReports.map((report) => (
                    <SummaryReportsDialogRow
                        key={report.id}
                        report={report}
                        isOwned={report.createdBy === userId}
                        isRenaming={editingReportId === report.id}
                        isConfirmingDelete={pendingDeleteReport?.id === report.id}
                        onOpen={onOpen}
                        onStartRename={startRename}
                        onCommitRename={commitRename}
                        onCancelRename={() => setEditingReportId(null)}
                        onStartDelete={setPendingDeleteReport}
                        onConfirmDelete={confirmDelete}
                        onCancelDelete={() => setPendingDeleteReport(null)}
                        onShare={onShare}
                    />
                ))}
                {visibleReports.length === 0 && (
                    // Centred: the floor makes an empty tab a lot of empty room, and a line
                    // pinned to its top corner reads as a mistake.
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ p: 4, textAlign: 'center' }}
                    >
                        No saved reports found.
                    </Typography>
                )}
            </List>
        </Box>
    );
}
